import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faSpinner, faPlus, faBell, faCheck } from '@/fontawesome.ts';
import { faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
import Modal from '@/components/ui/Modal.tsx';
import Checkbox from '@/components/ui/Checkbox';
import RecommendationStatsModal from '@/components/admin/recommendations/RecommendationStatsModal.tsx';
import CreateMissingSymbolsModal, { parseTickerInput } from '@/components/admin/recommendations/CreateMissingSymbolsModal.tsx';
import MultiCreateTable from '@/components/admin/recommendations/MultiCreateTable.tsx';
import type { SymbolEntry } from '@/components/admin/recommendations/CreateMissingSymbolsModal.tsx';
import { showSuccessToast, handleAPIError } from '@/utils/toasts.ts';
import { APIRoutes } from '@/services/api.routes.ts';
import { toCurrencyCentValue, toUSDCentValue, toRFCDateValue } from '@/utils/helpers.ts';
import type { RecommendationEarnings, RecommendationCreateStats, Symbol as SymbolModel } from '@/models';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import type { CreateBatchEarningsRecommendationRequest, CreateSingleEarningsRecommendationRequest } from '@/modelsAPI';

interface EarningTradeInput {
  id: string;
  symbolTicker: string;
  strikePrice: string;
  startDate: string;
  endDate: string;
  premium: string;
  boost: boolean;
  free: boolean;
  comment: string|null;
  currentPrice: string;
}

interface CreateMultipleEarningTradesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTradesCreated: (trades: RecommendationEarnings[]) => void;
}

export default function CreateMultipleEarningTradesModal({
  isOpen,
  onClose,
  onTradesCreated
}: CreateMultipleEarningTradesModalProps) {
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getNextFriday = (): Date => {
    const today = new Date();
    const dayOfWeek = today.getDay();

    let daysUntilFriday = (5 - dayOfWeek + 7) % 7;
    if (daysUntilFriday === 0) daysUntilFriday = 7;

    const nextFriday = new Date(today);
    nextFriday.setDate(today.getDate() + daysUntilFriday);
    return nextFriday;
  };

  const today = formatDateForInput(new Date());
  const nextFriday = formatDateForInput(getNextFriday());

  const [discordPreMessage, setDiscordPreMessage] = useState('');
  const [discordPostMessage, setDiscordPostMessage] = useState('');
  const [trades, setTrades] = useState<EarningTradeInput[]>([
    {
      id: Date.now().toString(),
      symbolTicker: '',
      strikePrice: '',
      startDate: today,
      endDate: nextFriday,
      premium: '',
      boost: false,
      free: false,
      comment: '',
      currentPrice: '',
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [createdStats, setCreatedStats] = useState<RecommendationCreateStats | null>(null);
  const [createdTrades, setCreatedTrades] = useState<RecommendationEarnings[]>([]);

  // Symbol cache
  const symbolCacheRef = useRef<Record<string, SymbolModel | null>>({});
  const [symbolCacheVersion, setSymbolCacheVersion] = useState(0);

  // Missing symbols modal state
  const [showMissingSymbolsModal, setShowMissingSymbolsModal] = useState(false);
  const [symbolEntries, setSymbolEntries] = useState<SymbolEntry[]>([]);

  // Stats preview state
  const [estimatedStats, setEstimatedStats] = useState<RecommendationCreateStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [totalUserCount, setTotalUserCount] = useState<number | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleAddRow = () => {
    setTrades(prevTrades => [...prevTrades, {
      id: Date.now().toString(),
      symbolTicker: '',
      strikePrice: '',
      startDate: today,
      endDate: nextFriday,
      premium: '',
      boost: false,
      free: false,
      comment: '',
      currentPrice: '',
    }]);
  };

  const handleRemoveRow = (id: string) => {
    setTrades(prevTrades => {
      if (prevTrades.length > 1) {
        return prevTrades.filter(trade => trade.id !== id);
      }
      return prevTrades;
    });
  };

  const handleUpdateRow = (id: string, field: keyof EarningTradeInput, value: string | boolean) => {
    setTrades(prevTrades => prevTrades.map(trade =>
      trade.id === id ? { ...trade, [field]: value } : trade
    ));
  };

  const lookupSymbol = async (ticker: string, tradeId: string, retryCount = 0) => {
    const rawInput = ticker.trim().toUpperCase();
    const { ticker: parsedTicker } = parseTickerInput(rawInput);

    if (!parsedTicker || parsedTicker.length > 10 || !/^[A-Z0-9]+$/.test(parsedTicker)) {
      setTrades(prev => prev.map(trade =>
        trade.id === tradeId ? { ...trade, currentPrice: parsedTicker ? '?' : '' } : trade
      ));
      return;
    }

    if (symbolCacheRef.current[rawInput] !== undefined) {
      const cached = symbolCacheRef.current[rawInput];
      if (cached !== null) {
        const currentPrice = cached.currentData?.price !== null && cached.currentData?.price !== undefined
          ? cached.currentData.price.toFixed(2)
          : '?';
        setTrades(prev => prev.map(trade =>
          trade.id === tradeId ? { ...trade, currentPrice } : trade
        ));
      } else {
        setTrades(prev => prev.map(trade =>
          trade.id === tradeId ? { ...trade, currentPrice: '?' } : trade
        ));
      }
      return;
    }

    const maxRetries = 1;

    try {
      setLoading(true);

      const response = await APIRoutes.listSymbols({
        ticker: parsedTicker,
        limit: 1
      });

      const exactMatch = response.symbols.find(s => s.ticker === parsedTicker);

      if (exactMatch !== undefined) {
        symbolCacheRef.current[rawInput] = exactMatch;
        const currentPrice = exactMatch.currentData?.price !== null && exactMatch.currentData?.price !== undefined
          ? exactMatch.currentData.price.toFixed(2)
          : '?';
        setTrades(prev => prev.map(trade =>
          trade.id === tradeId ? { ...trade, currentPrice } : trade
        ));
      } else {
        symbolCacheRef.current[rawInput] = null;
        setTrades(prev => prev.map(trade =>
          trade.id === tradeId ? { ...trade, currentPrice: '?' } : trade
        ));
      }
      setSymbolCacheVersion(v => v + 1);
      setLoading(false);
    } catch (err) {
      const isRetryableError = err instanceof Error &&
        (!err.message.includes('400') && !err.message.includes('404') && !err.message.includes('401'));

      console.error(`Symbol lookup failed (attempt ${retryCount + 1}/${maxRetries + 1}):`, err);

      if (retryCount < maxRetries && isRetryableError) {
        const delay = 800;
        setTimeout(() => {
          lookupSymbol(ticker, tradeId, retryCount + 1);
        }, delay);
        return;
      }

      symbolCacheRef.current[rawInput] = null;
      setSymbolCacheVersion(v => v + 1);
      setTrades(prev => prev.map(trade =>
        trade.id === tradeId ? { ...trade, currentPrice: '?' } : trade
      ));
      setLoading(false);
    }
  };

  const collectSymbolEntries = (): SymbolEntry[] => {
    const seen = new Map<string, SymbolEntry>();
    for (const trade of trades) {
      const rawInput = trade.symbolTicker.trim().toUpperCase();
      if (rawInput === '' || seen.has(rawInput)) continue;
      const { ticker, exchange } = parseTickerInput(rawInput);
      seen.set(rawInput, {
        rawInput,
        parsedTicker: ticker,
        parsedExchange: exchange,
        existingSymbol: symbolCacheRef.current[rawInput] ?? null
      });
    }
    return Array.from(seen.values());
  };

  const createRequest = (): [CreateBatchEarningsRecommendationRequest, string[]] => {
    const payloads: CreateSingleEarningsRecommendationRequest[] = [];
    const errors: string[] = [];

    for (const trade of trades) {
      if (!trade.symbolTicker.trim()) continue;

      const tradeErrors: string[] = [];

      if (parseFloat(trade.strikePrice) <= 0) {
        tradeErrors.push(`Strike Preis muss größer als 0 sein`);
      }

      if (parseFloat(trade.premium) <= 0) {
        tradeErrors.push(`Prämie muss größer als 0 sein`);
      }

      if (!trade.startDate || !trade.endDate) {
        tradeErrors.push(`Start- und Enddatum sind erforderlich`);
      } else {
        const startDateTime = new Date(trade.startDate + 'T00:00:00');
        const endDateTime = new Date(trade.endDate + 'T23:59:59');

        if (startDateTime >= endDateTime) {
          tradeErrors.push(`Enddatum muss nach dem Startdatum liegen`);
        }
      }

      if (tradeErrors.length > 0) {
        errors.push(`${trade.symbolTicker}: ${tradeErrors.join(', ')}`);
        continue;
      }

      const rawInput = trade.symbolTicker.trim().toUpperCase();
      const cachedSymbol = symbolCacheRef.current[rawInput];

      const payload: CreateSingleEarningsRecommendationRequest = {
        symbolID:    cachedSymbol?.id ?? null,
        strikePrice: toCurrencyCentValue(parseFloat(trade.strikePrice)),
        startDate:   toRFCDateValue(trade.startDate),
        endDate:     toRFCDateValue(trade.endDate),
        premium:     toUSDCentValue(parseFloat(trade.premium)),
        boost:       trade.boost,
        free:        trade.free,
        comment:     (trade.comment?.trim() !== '' ? trade.comment?.trim() : null) ?? null,
      };

      payloads.push(payload);
    }

    const request = {
      recommendations:    payloads,
      discordPreMessage:  discordPreMessage.trim()  !== '' ? discordPreMessage.trim()  : null,
      discordPostMessage: discordPostMessage.trim() !== '' ? discordPostMessage.trim() : null,
    } satisfies CreateBatchEarningsRecommendationRequest;

    return [request, errors];
  };

  const submitRequest = async () => {
    const [request, errors] = createRequest();

    if (errors.length > 0) {
      setError(errors.join(' | '));
      return;
    }

    if (request.recommendations.length === 0) {
      setError('Bitte geben Sie mindestens einen Earning-Trade ein');
      return;
    }

    setLoading(true);
    try {
      const response = await APIRoutes.createEarningsRecommendations(request, false);
      const newTrades = response.recommendations;

      setCreatedTrades(newTrades);
      setCreatedStats(response.stats);
      setShowStatsModal(true);
    } catch (err) {
      setError('Fehler beim Erstellen der Earning-Trades');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const [request, errors] = createRequest();

    if (errors.length > 0) {
      setError(errors.join(' | '));
      return;
    }

    if (request.recommendations.length === 0) {
      setError('Bitte geben Sie mindestens einen Earning-Trade ein');
      return;
    }

    const allEntries = collectSymbolEntries();
    const missingSymbols = allEntries.filter(e => e.existingSymbol === null);

    if (missingSymbols.length > 0) {
      setSymbolEntries(allEntries);
      setShowMissingSymbolsModal(true);
      return;
    }

    await submitRequest();
  };

  const handleSymbolsReady = async (symbolMap: Record<string, SymbolModel>) => {
    for (const [rawInput, symbol] of Object.entries(symbolMap)) {
      symbolCacheRef.current[rawInput] = symbol;
    }
    setSymbolCacheVersion(v => v + 1);
    setShowMissingSymbolsModal(false);

    await submitRequest();
  };

  const isTradeValid = (trade: EarningTradeInput): boolean => {
    if (trade.symbolTicker.trim() === '') return false;

    const strikePrice = parseFloat(trade.strikePrice);
    const premium = parseFloat(trade.premium);
    if (isNaN(strikePrice) || strikePrice <= 0) return false;
    if (isNaN(premium) || premium <= 0) return false;
    if (!trade.startDate || !trade.endDate) return false;

    const startDateTime = new Date(trade.startDate + 'T00:00:00');
    const endDateTime = new Date(trade.endDate + 'T23:59:59');
    return startDateTime < endDateTime;
  };

  const allTradesValid = trades.every(isTradeValid);

  const fetchEstimatedStats = async () => {
    if (!allTradesValid) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    const [request, errors] = createRequest();

    if (errors.length > 0) {
      setEstimatedStats(null);
      setStatsError(errors[0]);
      setStatsLoading(false);
      return;
    }

    if (request.recommendations.length === 0) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);
    setStatsError(null);

    try {
      const response = await APIRoutes.createEarningsRecommendations(request, true);
      setEstimatedStats(response.stats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Statistiken';
      setStatsError(errorMessage);
      setEstimatedStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const tradesJson = JSON.stringify(trades);

  useEffect(() => {
    setStatsLoading(true);

    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchEstimatedStats();
    }, 500);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [tradesJson, discordPreMessage, discordPostMessage]);

  useEffect(() => {
    if (isOpen) {
      APIRoutes.listUsers({ limit: 1 }).then(response => {
        setTotalUserCount(response.total);
      }).catch(() => {
        setTotalUserCount(null);
      });
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!loading) {
      setTrades([{
        id: Date.now().toString(),
        symbolTicker: '',
        strikePrice: '',
        startDate: today,
        endDate: nextFriday,
        premium: '',
        boost: false,
        free: false,
        comment: '',
        currentPrice: '',
      }]);
      setDiscordPreMessage('');
      setDiscordPostMessage('');
      setError('');
      setShowStatsModal(false);
      setCreatedStats(null);
      setCreatedTrades([]);
      setEstimatedStats(null);
      setStatsError(null);
      setShowMissingSymbolsModal(false);
      setSymbolEntries([]);
      symbolCacheRef.current = {};
      setSymbolCacheVersion(0);
      onClose();
    }
  };

  const handleStatsModalClose = () => {
    onTradesCreated(createdTrades);
    showSuccessToast(`${createdTrades.length} Earning-Trade${createdTrades.length > 1 ? 's' : ''} erfolgreich erstellt`);
    handleClose();
  };

  void symbolCacheVersion;

  return (
    <>
    <Modal
      isOpen={isOpen && !showStatsModal && !showMissingSymbolsModal}
      onClose={handleClose}
      title="Neue Earning-Trades"
      icon={<FontAwesomeIcon icon={faChartLine} className="w-5 h-5 text-green-600" />}
      maxWidth="screen"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
        {/* Discord Pre Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Discord Nachricht (vor allen Tradingideen)
          </label>
          <textarea
            value={discordPreMessage}
            onChange={(e) => setDiscordPreMessage(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Optional: Nachricht, die vor allen Earning-Trades gesendet wird"
            disabled={loading}
          />
        </div>

        <MultiCreateTable
          rows={trades}
          keyExtractor={(trade) => trade.id}
          onRemove={(trade) => handleRemoveRow(trade.id)}
          removeDisabled={loading}
          rowLabel={(index) => `Trade ${index + 1}`}
          fields={[
            {
              key: 'ticker',
              header: 'Ticker *',
              primary: true,
              render: (trade) => {
                const rawInput = trade.symbolTicker.trim().toUpperCase();
                const symbolFound = rawInput !== '' && symbolCacheRef.current[rawInput] !== undefined && symbolCacheRef.current[rawInput] !== null;
                return (
                  <div className="relative w-full md:w-28">
                    <input
                      type="text"
                      value={trade.symbolTicker}
                      onChange={(e) => handleUpdateRow(trade.id, 'symbolTicker', e.target.value.toUpperCase())}
                      onBlur={(e) => lookupSymbol(e.target.value, trade.id)}
                      className="w-full md:w-28 px-2 py-1 pr-7 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      placeholder="AAPL"
                      disabled={loading}
                    />
                    {symbolFound && (
                      <FontAwesomeIcon icon={faCheck} className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-green-500" />
                    )}
                  </div>
                );
              }
            },
            {
              key: 'currentPrice',
              header: 'Aktueller Preis',
              render: (trade) => (
                <input
                  value={trade.currentPrice}
                  disabled={true}
                  className="w-full md:w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm cursor-not-allowed"
                />
              )
            },
            {
              key: 'strikePrice',
              header: 'Strike *',
              render: (trade) => (
                <input
                  type="number"
                  step="0.01"
                  value={trade.strikePrice}
                  onChange={(e) => handleUpdateRow(trade.id, 'strikePrice', e.target.value)}
                  className="w-full md:w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="100.00"
                  disabled={loading}
                  min="0"
                />
              )
            },
            {
              key: 'premium',
              header: 'Prämie / Kontrakt *',
              render: (trade) => (
                <input
                  type="number"
                  step="0.01"
                  value={trade.premium}
                  onChange={(e) => handleUpdateRow(trade.id, 'premium', e.target.value)}
                  className="w-full md:w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="5.00"
                  disabled={loading}
                  min="0"
                />
              )
            },
            {
              key: 'startDate',
              header: 'Startdatum *',
              render: (trade) => (
                <input
                  type="date"
                  value={trade.startDate}
                  onChange={(e) => handleUpdateRow(trade.id, 'startDate', e.target.value)}
                  className="w-full md:w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  disabled={loading}
                />
              )
            },
            {
              key: 'endDate',
              header: 'Enddatum *',
              render: (trade) => (
                <input
                  type="date"
                  value={trade.endDate}
                  onChange={(e) => handleUpdateRow(trade.id, 'endDate', e.target.value)}
                  className="w-full md:w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  disabled={loading}
                />
              )
            },
            {
              key: 'boost',
              header: 'Boost',
              align: 'center',
              mobileInline: true,
              render: (trade) => (
                <Checkbox
                  checked={trade.boost}
                  onChange={(checked) => handleUpdateRow(trade.id, 'boost', checked)}
                  colorTheme="green"
                />
              )
            },
            {
              key: 'free',
              header: 'Free',
              align: 'center',
              mobileInline: true,
              render: (trade) => (
                <Checkbox
                  checked={trade.free}
                  onChange={(checked) => handleUpdateRow(trade.id, 'free', checked)}
                  colorTheme="green"
                />
              )
            },
            {
              key: 'comment',
              header: 'Kommentar',
              render: (trade) => (
                <input
                  type="text"
                  value={trade.comment ?? ''}
                  onChange={(e) => handleUpdateRow(trade.id, 'comment', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Optional"
                  disabled={loading}
                />
              )
            }
          ]}
        />

        <button
          type="button"
          onClick={handleAddRow}
          className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 flex items-center space-x-2 text-left cursor-pointer"
          disabled={loading}
        >
          <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
          <span>Weiteren Earning-Trade hinzufügen</span>
        </button>

        {/* Discord Post Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Discord Nachricht (nach allen Tradingideen)
          </label>
          <textarea
            value={discordPostMessage}
            onChange={(e) => setDiscordPostMessage(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Optional: Nachricht, die nach allen Earning-Trades gesendet wird"
            disabled={loading}
          />
        </div>

        {/* Notification Options */}
        <label className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <FontAwesomeIcon icon={faBell} className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Benachrichtigungen</span>
              </div>
              {statsLoading && (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-gray-400" />
              )}
            </div>

            {statsError !== null && (
              <p className="text-xs text-red-500 dark:text-red-400 mt-2">{statsError}</p>
            )}

            <div className="mt-2 space-y-1">
              {estimatedStats !== null ? (
                <>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.discordMessages === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faDiscord} className="w-3 h-3 mr-2" />
                    Discord-Nachrichten: {estimatedStats.discordMessages}
                  </p>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.telegramMessages === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faTelegram} className="w-3 h-3 mr-2" />
                    Telegram-Nachrichten: {estimatedStats.telegramMessages}
                  </p>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.scnNotifications === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
                    SCN Push-Nachrichten: {estimatedStats.scnNotifications}
                  </p>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.pushNotifications === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
                    Push (App): {estimatedStats.pushNotifications}
                  </p>
                  <p className={`text-xs text-gray-500 dark:text-gray-400 flex items-center ${estimatedStats.mailNotifications === 0 ? 'opacity-50' : ''}`}>
                    <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3 mr-2" />
                    E-Mails an {estimatedStats.mailNotifications}{totalUserCount !== null ? ` von insgesamt ${totalUserCount} Nutzern` : ''}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faDiscord} className="w-3 h-3 mr-2" />
                    Sendet Discord-Nachrichten in die entsprechenden Channel
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faTelegram} className="w-3 h-3 mr-2" />
                    Sendet Telegram-Nachrichten
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
                    Sendet SCN Push-Benachrichtigungen an Nutzer, welche das konfiguriert haben
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-2" />
                    Sendet Push-Benachrichtigungen (App) an Nutzer, welche diese aktiviert haben
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3 mr-2" />
                    Sendet E-Mails an Nutzer
                  </p>
                </>
              )}
            </div>
          </div>
        </label>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !allTradesValid}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Erstelle Trades...
              </>
            ) : (
              'Earning-Trades erstellen'
            )}
          </button>
        </div>
      </form>
    </Modal>

    <CreateMissingSymbolsModal
      isOpen={showMissingSymbolsModal}
      onClose={() => setShowMissingSymbolsModal(false)}
      symbols={symbolEntries}
      onSymbolsReady={handleSymbolsReady}
    />

    {createdStats !== null && (
      <RecommendationStatsModal
        isOpen={showStatsModal}
        onClose={handleStatsModalClose}
        stats={createdStats}
        recommendationType="earnings"
        text={`${createdTrades.length} Earning-Empfehlung${createdTrades.length > 1 ? 'en' : ''} erfolgreich erstellt`}
      />
    )}
    </>
  );
}
